import React, { useState, useEffect } from 'react';
import { Alert, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { hasTrackExportAccess } from 'utils/auth';
import TrackExportUploadSection from './components/TrackExportUploadSection';
import TrackExportTaskTable from './components/TrackExportTaskTable';
import TrackExportErrorDrawer from './components/TrackExportErrorDrawer';
import { useTrackExportTasks } from './hooks/useTrackExportTasks';
import type { TrackExportErrorDetail } from './services/trackExportApi';

const STATUS_PARSING = 0;
const STATUS_VALIDATION_FAILED = 2;

const TrackExportPage: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [errorDrawerVisible, setErrorDrawerVisible] = useState(false);
  const [errorDetail, setErrorDetail] = useState<TrackExportErrorDetail | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentTaskNo, setCurrentTaskNo] = useState<string | null>(null);

  const hasAccess = hasTrackExportAccess();

  const { data, loading, error, refetch, triggerUploadSuccess } =
    useTrackExportTasks({
      page,
      size: pageSize,
      enablePolling: true,
      hasActiveProgressBar: uploading && !!currentTaskNo,
      enabled: hasAccess,
    });

  // 轮询时检查当前任务是否完成，完成则停止进度条
  useEffect(() => {
    if (!uploading || !currentTaskNo || !data?.content) return;
    const task = data.content.find((t) => t.taskNo === currentTaskNo);
    if (!task) return;
    if (task.status !== STATUS_PARSING) {
      setUploading(false);
      setCurrentTaskNo(null);
      setUploadProgress(100);
      refetch({ page: 1 });
    } else {
      setUploadProgress(60);
    }
  }, [uploading, currentTaskNo, data?.content, refetch]);

  // 5 分钟超时，避免进度条一直显示
  useEffect(() => {
    if (!uploading || !currentTaskNo) return;
    const timer = setTimeout(() => {
      setUploading(false);
      setCurrentTaskNo(null);
      setUploadProgress(100);
      message.warning(t('central.trackExport.upload.processTimeout'));
    }, 5 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [uploading, currentTaskNo, t]);

  const handleUploadStart = () => {
    setUploading(true);
    setUploadProgress(0);
    setCurrentTaskNo(null);
  };

  const handleUploadProgress = (percent: number) => {
    setUploadProgress(percent);
  };

  const handleUploadComplete = (payload: { taskNo: string; status: number; message: string }) => {
    setPage(1);
    triggerUploadSuccess();
    refetch({ page: 1 });
    if (payload.status === STATUS_VALIDATION_FAILED) {
      setUploading(false);
      setCurrentTaskNo(null);
      setUploadProgress(0);
    } else {
      setCurrentTaskNo(payload.taskNo);
    }
  };

  const handleUploadError = () => {
    setUploading(false);
    setUploadProgress(0);
    setCurrentTaskNo(null);
  };

  const handlePageChange = (p: number, size: number) => {
    setPage(p);
    setPageSize(size);
  };

  const handleShowError = (detail: TrackExportErrorDetail) => {
    setErrorDetail(detail);
    setErrorDrawerVisible(true);
  };

  if (!hasAccess) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('central.trackExport.noAccess')}
        className="mb-4"
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <TrackExportUploadSection
        uploading={uploading}
        uploadProgress={uploadProgress}
        currentTaskNo={currentTaskNo}
        onUploadStart={handleUploadStart}
        onUploadProgress={handleUploadProgress}
        onUploadComplete={handleUploadComplete}
        onUploadError={handleUploadError}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message={error.message}
          className="mb-4"
        />
      )}

      <TrackExportTaskTable
        data={data?.content ?? []}
        loading={loading}
        page={page}
        pageSize={pageSize}
        totalElements={data?.totalElements ?? 0}
        onPageChange={handlePageChange}
        onRefresh={refetch}
        onShowError={handleShowError}
      />

      <TrackExportErrorDrawer
        visible={errorDrawerVisible}
        onClose={() => {
          setErrorDrawerVisible(false);
          setErrorDetail(null);
        }}
        errorDetail={errorDetail}
      />
    </div>
  );
};

export default TrackExportPage;
